import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Menu } from '@base-ui/react/menu';
import { useDirection } from '@base-ui/react/direction-provider';
import { Check, ChevronRight } from 'lucide-react';
import { boxesEqual, useGlide } from './useGlide.js';

// Fluid Functionalism dropdown, cut down to Base UI. The popup is non-modal
// so the page keeps scrolling. One highlight glides between rows; a second
// overlay stays on the checked rows, and touching checks share it. Motion,
// the weight animation, the search field and the inline panel are left out.

const SizeContext = createContext('default');
const SelectionContext = createContext(false);

export function DropdownMenu({
  open,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  size = 'default',
  children,
}) {
  return (
    <SizeContext.Provider value={size}>
      <Menu.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={(next) => onOpenChange?.(next)}
        disabled={disabled}
        modal={false}
      >
        {children}
      </Menu.Root>
    </SizeContext.Provider>
  );
}

export function DropdownTrigger({ render, children, disabled, ...props }) {
  return (
    <Menu.Trigger {...props} disabled={disabled} render={render}>
      {children}
    </Menu.Trigger>
  );
}

export function DropdownContent({
  children,
  className,
  checkedIndex,
  checkedIndices,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
  alignOffset = 0,
  sub = false,
}) {
  const size = useContext(SizeContext);
  const multiple = checkedIndices != null;
  const { listRef, highlight, selected, highlightOn, highlightMoves, selectedMoves } = useMenuLayers();

  return (
    <Menu.Portal>
      <Menu.Positioner
        className="edl-menu__positioner"
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
      >
        <Menu.Popup
          className={['edl-menu__popup', className].filter(Boolean).join(' ')}
          data-size={size}
          data-sub={sub ? '' : undefined}
        >
          <div ref={listRef} className="edl-menu__list">
            {selected.map((box, i) => (
              <span
                key={i}
                className="edl-menu__selected"
                data-shown=""
                data-move={selectedMoves ? '' : undefined}
                style={boxStyle(box)}
                aria-hidden="true"
              />
            ))}
            <span
              className="edl-menu__highlight"
              data-shown={highlightOn ? '' : undefined}
              data-move={highlightMoves ? '' : undefined}
              style={highlight ? boxStyle(highlight) : undefined}
              aria-hidden="true"
            />
            <SelectionContext.Provider value={multiple}>
              <Menu.RadioGroup value={multiple ? null : (checkedIndex ?? null)} className="edl-menu__group">
                {children}
              </Menu.RadioGroup>
            </SelectionContext.Provider>
          </div>
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  );
}

export function DropdownSub({ open, defaultOpen = false, onOpenChange, children }) {
  return (
    <Menu.SubmenuRoot
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={(next) => onOpenChange?.(next)}
    >
      {children}
    </Menu.SubmenuRoot>
  );
}

export function DropdownSubTrigger({ index, label, icon: Icon, disabled = false, className }) {
  const size = useContext(SizeContext);
  return (
    <Menu.SubmenuTrigger
      disabled={disabled}
      label={label}
      className={['edl-menu__item', className].filter(Boolean).join(' ')}
      data-index={index}
    >
      <Row icon={Icon} label={label} size={size} chevron />
    </Menu.SubmenuTrigger>
  );
}

export function DropdownSubContent({ sideOffset = 6, alignOffset = -4, ...props }) {
  const direction = useDirection();
  return (
    <DropdownContent
      {...props}
      side={direction === 'rtl' ? 'left' : 'right'}
      align="start"
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      sub
    />
  );
}

export function MenuItem({
  icon: Icon,
  label,
  index,
  checked,
  onSelect,
  disabled = false,
  closeOnClick,
  className,
  href,
  trailingIcon: TrailingIcon,
}) {
  const size = useContext(SizeContext);
  const multiple = useContext(SelectionContext);
  const selectable = typeof checked === 'boolean';
  const itemProps = {
    disabled,
    label,
    closeOnClick: closeOnClick ?? !multiple,
    onClick: () => onSelect?.(),
    className: ['edl-menu__item', className].filter(Boolean).join(' '),
    'data-index': index,
  };
  const body = <Row icon={Icon} trailingIcon={TrailingIcon} label={label} size={size} checked={selectable ? checked : undefined} />;

  if (multiple && selectable) {
    return (
      <Menu.CheckboxItem {...itemProps} checked={checked}>
        {body}
      </Menu.CheckboxItem>
    );
  }
  if (selectable) {
    return (
      <Menu.RadioItem {...itemProps} value={index}>
        {body}
      </Menu.RadioItem>
    );
  }
  // A disabled item renders as a plain row, so a link under it cannot open.
  const link = href && !disabled;
  return <Menu.Item {...itemProps} nativeButton={link ? false : undefined} render={link ? <a href={href} target="_blank" rel="noreferrer" /> : undefined}>{body}</Menu.Item>;
}

function Row({ icon: Icon, trailingIcon: TrailingIcon, label, size, checked, chevron = false }) {
  const iconSize = size === 'compact' ? 14 : 16;
  const trailSize = 12;
  return (
    <>
      {Icon ? <Icon size={iconSize} strokeWidth={1.5} aria-hidden="true" /> : null}
      <span className="edl-menu__label">{label}</span>
      {TrailingIcon ? <TrailingIcon className="edl-menu__trail" size={trailSize} strokeWidth={1.5} aria-hidden="true" /> : null}
      {checked !== undefined ? (
        <span className="edl-menu__check" aria-hidden="true">
          {checked ? <Check size={iconSize} strokeWidth={2} /> : null}
        </span>
      ) : null}
      {chevron ? <ChevronRight className="edl-menu__chevron" size={iconSize} strokeWidth={1.5} aria-hidden="true" /> : null}
    </>
  );
}

// The list unmounts while the popup is closed, so the watcher starts from
// the ref callback. A submenu's trigger stays lit while that submenu is open,
// until the pointer leaves its safe area and another row is highlighted.
// Both layers snap into their first box. After that, a new row glides and a
// remeasure of the same rows snaps.
function useMenuLayers() {
  const observer = useRef(null);
  const resizeObserver = useRef(null);
  const settled = useRef(false);
  const arm = useRef(0);
  const [highlight, setHighlight] = useState(null);
  const [selected, setSelected] = useState([]);
  const [highlightKey, setHighlightKey] = useState(null);
  const [checkedKey, setCheckedKey] = useState(null);
  const [highlightOn, setHighlightOn] = useState(false);
  const highlightMoves = useGlide(highlightKey);
  const selectedMoves = useGlide(checkedKey);

  const listRef = useCallback((node) => {
    observer.current?.disconnect();
    resizeObserver.current?.disconnect();
    cancelAnimationFrame(arm.current);
    observer.current = null;
    resizeObserver.current = null;
    if (!node) {
      settled.current = false;
      setHighlightKey(null);
      setCheckedKey(null);
      setHighlight(null);
      setSelected([]);
      setHighlightOn(false);
      return;
    }
    const place = () => {
      const hot = node.querySelector('.edl-menu__item[data-highlighted]:not([data-disabled])');
      const openSub = node.querySelector('.edl-menu__item[data-popup-open]:not([data-disabled])');
      const lit = openSub && (!hot || hot === openSub) ? openSub : hot;
      const checks = [...node.querySelectorAll('.edl-menu__item[data-checked]')];
      if (lit) {
        const next = boxOf(lit);
        setHighlight((prev) => (boxesEqual(prev, next) ? prev : next));
        setHighlightOn(true);
      } else {
        setHighlightOn(false);
      }
      const runs = mergeRuns(checks);
      setSelected((prev) => (sameRuns(prev, runs) ? prev : runs));
      if (!settled.current) return;
      const items = [...node.querySelectorAll('.edl-menu__item')];
      const nextHighlight = lit ? String(items.indexOf(lit)) : null;
      const nextChecked = checks.map((item) => String(items.indexOf(item))).join(',');
      setHighlightKey((prev) => (prev === nextHighlight ? prev : nextHighlight));
      setCheckedKey((prev) => (prev === nextChecked ? prev : nextChecked));
    };
    place();
    arm.current = requestAnimationFrame(() => {
      settled.current = true;
      place();
    });
    const next = new MutationObserver(place);
    next.observe(node, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-highlighted', 'data-checked', 'data-disabled', 'data-popup-open'],
    });
    observer.current = next;
    const resize = new ResizeObserver(place);
    resize.observe(node);
    resizeObserver.current = resize;
  }, []);

  return { listRef, highlight, selected, highlightOn: highlightOn && highlightKey != null, highlightMoves, selectedMoves };
}

function boxOf(item) {
  return { x: item.offsetLeft, y: item.offsetTop, w: item.offsetWidth, h: item.offsetHeight };
}

function boxStyle(box) {
  return { transform: `translate(${box.x}px, ${box.y}px)`, width: box.w, height: box.h };
}

function sameRuns(a, b) {
  return a.length === b.length && a.every((run, i) => boxesEqual(run, b[i]));
}

// Checked rows that touch become one background. A gap (an unchecked row
// between them) starts a new run.
function mergeRuns(items) {
  const runs = [];
  items.forEach((item) => {
    const box = boxOf(item);
    const run = runs[runs.length - 1];
    if (run && box.y <= run.y + run.h + 1) {
      run.h = box.y + box.h - run.y;
      run.w = Math.max(run.w, box.w);
      run.x = Math.min(run.x, box.x);
      return;
    }
    runs.push(box);
  });
  return runs;
}
